/**
 * @file MovingCarAnimation.js
 * @description Componente de animación cinematográfica de auto en movimiento o detenido con humo
 * @module components/cards/MovingCarAnimation
 * 
 * Características principales:
 * - Animación de auto naranja en carretera con paisaje parallax
 * - Modo "en movimiento": paisaje animado con elementos en múltiples capas (árboles, arbustos, carretera)
 * - Modo "detenido": auto estático con animación de humo del cofre
 * - Sistema de capas Z-index: fondo → elementos lejanos → carretera → auto → elementos cercanos → humo
 * - Efectos de profundidad mediante velocidades de animación diferenciadas
 * - SVG optimizado para renderizado eficiente
 * 
 * Capas del sistema (Z-index):
 * - z-0: Montañas de fondo (estáticas)
 * - z-5: Elementos lejanos detrás del auto (árboles/arbustos lentos)
 * - z-10: Carretera con líneas animadas
 * - z-20: Auto naranja principal
 * - z-30: Elementos cercanos que cruzan adelante (árboles/arbustos rápidos)
 * - z-40: Difuminados de integración con la tarjeta
 * - z-50: Humo del cofre (solo modo detenido)
 * 
 * Performance:
 * - Animaciones CSS puras (sin JavaScript)
 * - SVG con drop-shadow para sombras eficientes
 * - Gradientes de difuminado para integración visual con la tarjeta padre
 */

import React from 'react';

/**
 * Componente MovingCarAnimation - Animación de vehículo con contexto visual
 * 
 * Renderiza una escena cinematográfica de un auto en una carretera con paisaje animado.
 * El componente cambia dinámicamente entre dos estados:
 * 
 * **Modo "En movimiento" (isMoving=true):**
 * - Auto naranja con movimiento sutil vertical
 * - Carretera con líneas animadas
 * - Elementos del paisaje moviéndose en parallax (árboles, arbustos, postes)
 * - Elementos lejanos (lentos) y cercanos (rápidos) para efecto de profundidad
 * 
 * **Modo "Detenido" (isMoving=false):**
 * - Auto estático
 * - Sin animación de paisaje
 * - Humo saliendo del cofre (indica avería/problema)
 * - Humo con múltiples partículas escalonadas para efecto continuo
 * 
 * @component
 * @param {Object} props
 * @param {boolean} [props.isMoving=true] - Si el auto está en movimiento o detenido
 * @param {('small'|'medium'|'large')} [props.size='medium'] - Tamaño del auto en pantalla
 * @returns {JSX.Element} Animación del auto con paisaje
 */
const MovingCarAnimation = ({ isMoving = 'true', size = 'medium' }) => {
  /**
   * Configuración de tamaños disponibles para el auto
   * Se aplican como clases Tailwind al contenedor del SVG del auto
   */
  const sizeClasses = {
    small: 'w-10 h-10',    // 40x40px - Para StatCards compactas
    medium: 'w-12 h-12',   // 48x48px - Tamaño por defecto
    large: 'w-14 h-14'     // 56x56px - Para vistas destacadas
  };

  return (
    <div className="relative w-full h-6 rounded-lg" style={{ overflow: 'visible' }}>
      {/* 
        Contenedor interno con overflow hidden
        Mantiene el paisaje dentro de los límites, pero permite que el humo escape
      */}
      <div className="relative w-full h-full overflow-hidden rounded-lg">
        
        {/* ===== CAPA 0: FONDO ESTÁTICO (z-0) ===== */}
        
        {/* Gradiente de cielo - Se difumina con la tarjeta padre */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50 to-green-100 opacity-70"></div>
        
        {/* Difuminado superior - Integración con la tarjeta */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-white to-transparent"></div>
        
        {/* Montañas sutiles en el horizonte (SVG estático) */}
        <div className="absolute bottom-0 left-0 right-0 h-4 z-0">
          <svg className="w-full h-full" viewBox="0 0 400 16" preserveAspectRatio="none">
            {/* Capa de montañas lejanas */}
            <path d="M0,16 L0,12 L60,6 L120,10 L180,4 L240,8 L300,3 L360,7 L400,5 L400,16 Z" 
                  fill="#E5E7EB" opacity="0.3"/>
            {/* Capa de montañas más cercanas */}
            <path d="M0,16 L0,14 L80,8 L160,12 L240,6 L320,10 L400,8 L400,16 Z" 
                  fill="#D1D5DB" opacity="0.2"/>
          </svg>
        </div>
        
        {/* ===== CAPA 1: ELEMENTOS LEJANOS DETRÁS DEL AUTO (z-5) ===== */}
        {/* Solo se muestran cuando el auto está en movimiento */}
        {isMoving && (
          <div className="absolute top-0 left-0 right-0 bottom-3 z-5">
            
            {/* Árbol lejano 1 - Animación lenta (8s) */}
            <div className="absolute animate-far-tree-1" style={{ top: '4px' }}>
              <svg width="6" height="8" viewBox="0 0 6 8">
                <rect x="2" y="6" width="2" height="2" fill="#8B5A2B"/>
                <circle cx="3" cy="4" r="2" fill="#22C55E" opacity="0.4"/>
              </svg>
            </div>
            
            {/* Árbol lejano 2 - Animación lenta (9s, delay 3s) */}
            <div className="absolute animate-far-tree-2" style={{ top: '6px' }}>
              <svg width="5" height="6" viewBox="0 0 5 6">
                <rect x="2" y="5" width="1" height="1" fill="#8B5A2B"/>
                <circle cx="2.5" cy="3" r="1.5" fill="#16A34A" opacity="0.3"/>
              </svg>
            </div>
            
            {/* Arbusto lejano - Animación lenta (7s, delay 2s) */}
            <div className="absolute animate-far-bush-1" style={{ top: '8px' }}>
              <div className="w-3 h-1 bg-green-400 rounded-full opacity-40" />
            </div>
          </div>
        )}
        
        {/* ===== CAPA 2: CARRETERA (z-10) ===== */}
        {/* Línea horizontal con marcas viales animadas */}
        <div className="absolute bottom-2 left-0 right-0 h-px bg-gray-300 overflow-hidden z-10">
          <div 
            className={`absolute top-0 h-full w-full ${isMoving ? 'animate-road-moving' : ''}`}
            style={{ 
              // Patrón de líneas discontinuas de carretera
              background: 'repeating-linear-gradient(to right, #D1D5DB 0%, #D1D5DB 6px, #F3F4F6 6px, #F3F4F6 12px)'
            }}
          ></div>
        </div>
        
        {/* ===== CAPA 3: AUTO NARANJA PRINCIPAL (z-20) ===== */}
        <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 ${sizeClasses[size]} ${
          isMoving ? 'animate-car-subtle' : ''
        } z-20`}>
          <svg 
            viewBox="0 0 100 70" 
            className="w-full h-full"
            style={{
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))' // Sombra del auto
            }}
          >
            {/* Cuerpo inferior del auto - Naranja oscuro */}
            <rect x="8" y="45" width="75" height="18" rx="3" fill="#EA580C" />
            
            {/* Cabina principal - Naranja brillante */}
            <rect x="18" y="30" width="55" height="20" rx="6" fill="#F97316" />
            
            {/* Ventanas - Blanco semi-transparente */}
            <rect x="22" y="33" width="16" height="12" rx="2" fill="#F8FAFC" opacity="0.9" />
            <rect x="42" y="33" width="16" height="12" rx="2" fill="#F8FAFC" opacity="0.9" />
            <rect x="62" y="33" width="8" height="12" rx="2" fill="#F8FAFC" opacity="0.9" />
            
            {/* Llanta trasera con llanta y rin */}
            <circle cx="25" cy="63" r="7" fill="#1E293B" />
            <circle cx="25" cy="63" r="4" fill="#475569" />
            
            {/* Llanta delantera con llanta y rin */}
            <circle cx="70" cy="63" r="7" fill="#1E293B" />
            <circle cx="70" cy="63" r="4" fill="#475569" />
            
            {/* Faros delanteros - Amarillo suave */}
            <circle cx="85" cy="48" r="2" fill="#FEF3C7" opacity="0.9" />
            <circle cx="85" cy="54" r="2" fill="#FEF3C7" opacity="0.9" />
            
            {/* Luz trasera - Rojo */}
            <rect x="12" y="48" width="6" height="4" rx="1" fill="#DC2626" opacity="0.8" />
          </svg>
        </div>
        
        {/* ===== CAPA 4: ELEMENTOS CERCANOS DELANTE DEL AUTO (z-30) ===== */}
        {/* Cruzan la calle desde abajo, más rápidos que los elementos lejanos */}
        {isMoving && (
          <div className="absolute bottom-0 left-0 right-0 h-6 z-30">
            
            {/* Árbol cercano 1 - Rápido (4s) */}
            <div className="absolute animate-front-tree-1" style={{ bottom: '-2px' }}>
              <svg width="10" height="8" viewBox="0 0 10 8">
                <rect x="4" y="4" width="2" height="4" fill="#8B5A2B"/>
                <circle cx="5" cy="3" r="3" fill="#22C55E" opacity="0.8"/>
              </svg>
            </div>
            
            {/* Árbol cercano 2 - Rápido (5s, delay 2s) */}
            <div className="absolute animate-front-tree-2" style={{ bottom: '-1px' }}>
              <svg width="8" height="6" viewBox="0 0 8 6">
                <rect x="3" y="3" width="2" height="3" fill="#8B5A2B"/>
                <circle cx="4" cy="2.5" r="2.5" fill="#16A34A" opacity="0.7"/>
              </svg>
            </div>
            
            {/* Arbusto cercano 1 - Muy rápido (3s, delay 1s) */}
            <div className="absolute animate-front-bush-1" style={{ bottom: '0px' }}>
              <div className="w-5 h-3 bg-green-500 rounded-full opacity-70" />
            </div>
            
            {/* Arbusto cercano 2 - Muy rápido (3.5s, delay 3s) */}
            <div className="absolute animate-front-bush-2" style={{ bottom: '0px' }}>
              <div className="w-4 h-2 bg-green-600 rounded-full opacity-60" />
            </div>
            
            {/* Poste/señal ocasional - Velocidad media (6s, delay 4s) */}
            <div className="absolute animate-front-post-1" style={{ bottom: '0px' }}>
              <div className="w-1 h-4 bg-gray-600 opacity-60" />
            </div>
          </div>
        )}
        
        {/* ===== CAPA 5: DIFUMINADO INFERIOR (z-40) ===== */}
        {/* Integración visual con la tarjeta padre */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-white to-transparent z-40"></div>
      </div>
      
      {/* ===== CAPA 6: HUMO DEL COFRE (z-50) ===== */}
      {/* IMPORTANTE: Fuera del contenedor con overflow hidden para que escape hacia arriba */}
      {!isMoving && (
        <div className="absolute" style={{ 
          bottom: '8px',
          left: '50%',
          transform: 'translateX(-15px) translateY(-5px)', // Posicionado en el cofre del auto
          zIndex: 50
        }}>
          <svg width="50" height="60" viewBox="0 0 50 60" style={{ overflow: 'visible' }}>
            {/* 
              Sistema de partículas de humo escalonadas
              8 partículas con diferentes delays crean un flujo continuo
              Cada partícula:
              - Sube (cy: 55 → -10)
              - Se expande (r: ~1.5 → ~6)
              - Se difumina (opacity: ~0.6 → 0)
              - Se desplaza lateralmente (cx: drift hacia la izquierda)
            */}
            
            {/* GRUPO 1: Partículas principales (delays 0s, 0.3s, 0.6s) */}
            <circle cx="25" cy="55" r="1.8" fill="#4B5563" opacity="0.8">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0.6;0.3;0" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="r" values="1.8;3.5;5;7" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="cx" values="25;22;18;14" dur="2.5s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="27" cy="55" r="1.6" fill="#6B7280" opacity="0.7">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.8s" repeatCount="indefinite" begin="0.3s" />
              <animate attributeName="opacity" values="0.7;0.5;0.25;0" dur="2.8s" repeatCount="indefinite" begin="0.3s" />
              <animate attributeName="r" values="1.6;3;4.5;6.5" dur="2.8s" repeatCount="indefinite" begin="0.3s" />
              <animate attributeName="cx" values="27;24;20;16" dur="2.8s" repeatCount="indefinite" begin="0.3s" />
            </circle>
            
            <circle cx="23" cy="55" r="1.4" fill="#4B5563" opacity="0.6">
              <animate attributeName="cy" values="55;40;20;-10" dur="3s" repeatCount="indefinite" begin="0.6s" />
              <animate attributeName="opacity" values="0.6;0.4;0.2;0" dur="3s" repeatCount="indefinite" begin="0.6s" />
              <animate attributeName="r" values="1.4;2.8;4.2;6" dur="3s" repeatCount="indefinite" begin="0.6s" />
              <animate attributeName="cx" values="23;20;16;12" dur="3s" repeatCount="indefinite" begin="0.6s" />
            </circle>
            
            {/* GRUPO 2: Partículas secundarias para llenar huecos (delays 0.9s, 1.2s) */}
            <circle cx="26" cy="55" r="1.2" fill="#6B7280" opacity="0.5">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.7s" repeatCount="indefinite" begin="0.9s" />
              <animate attributeName="opacity" values="0.5;0.35;0.15;0" dur="2.7s" repeatCount="indefinite" begin="0.9s" />
              <animate attributeName="r" values="1.2;2.5;3.8;5.5" dur="2.7s" repeatCount="indefinite" begin="0.9s" />
              <animate attributeName="cx" values="26;23;19;15" dur="2.7s" repeatCount="indefinite" begin="0.9s" />
            </circle>
            
            <circle cx="24" cy="55" r="1.3" fill="#4B5563" opacity="0.6">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.9s" repeatCount="indefinite" begin="1.2s" />
              <animate attributeName="opacity" values="0.6;0.4;0.18;0" dur="2.9s" repeatCount="indefinite" begin="1.2s" />
              <animate attributeName="r" values="1.3;2.6;4;5.8" dur="2.9s" repeatCount="indefinite" begin="1.2s" />
              <animate attributeName="cx" values="24;21;17;13" dur="2.9s" repeatCount="indefinite" begin="1.2s" />
            </circle>
            
            {/* GRUPO 3: Partículas de relleno (delays 1.5s, 1.8s) */}
            <circle cx="25.5" cy="55" r="1.1" fill="#6B7280" opacity="0.4">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.6s" repeatCount="indefinite" begin="1.5s" />
              <animate attributeName="opacity" values="0.4;0.3;0.12;0" dur="2.6s" repeatCount="indefinite" begin="1.5s" />
              <animate attributeName="r" values="1.1;2.2;3.5;5.2" dur="2.6s" repeatCount="indefinite" begin="1.5s" />
              <animate attributeName="cx" values="25.5;22.5;18.5;14.5" dur="2.6s" repeatCount="indefinite" begin="1.5s" />
            </circle>
            
            <circle cx="22.5" cy="55" r="1.0" fill="#4B5563" opacity="0.45">
              <animate attributeName="cy" values="55;40;20;-10" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
              <animate attributeName="opacity" values="0.45;0.32;0.14;0" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
              <animate attributeName="r" values="1.0;2.1;3.3;5" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
              <animate attributeName="cx" values="22.5;19.5;15.5;11.5" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
            </circle>
            
            {/* GRUPO 4: Partícula final para completar ciclo (delay 2.1s) */}
            <circle cx="27.5" cy="55" r="1.5" fill="#6B7280" opacity="0.55">
              <animate attributeName="cy" values="55;40;20;-10" dur="2.4s" repeatCount="indefinite" begin="2.1s" />
              <animate attributeName="opacity" values="0.55;0.38;0.16;0" dur="2.4s" repeatCount="indefinite" begin="2.1s" />
              <animate attributeName="r" values="1.5;2.9;4.3;6.2" dur="2.4s" repeatCount="indefinite" begin="2.1s" />
              <animate attributeName="cx" values="27.5;24.5;20.5;16.5" dur="2.4s" repeatCount="indefinite" begin="2.1s" />
            </circle>
          </svg>
        </div>
      )}

      {/* 
        ===== DEFINICIONES DE ANIMACIONES CSS =====
        Todas las animaciones se definen aquí usando dangerouslySetInnerHTML
        para evitar el warning de React sobre tags <style> sin children
      */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* ========== ANIMACIONES DE CARRETERA ========== */
        
        /**
         * Animación de líneas de carretera moviéndose
         * Crea ilusión de movimiento al desplazar el patrón repetitivo
         */
        @keyframes road-moving {
          0% { transform: translateX(0px); }
          100% { transform: translateX(-12px); }
        }
        
        /**
         * Animación sutil del auto en movimiento
         * Pequeño rebote vertical para simular viaje en carretera
         */
        @keyframes car-subtle {
          0%, 100% { transform: translateX(-50%) translateY(0px); }
          50% { transform: translateX(-50%) translateY(-1px); }
        }
        
        /* ========== ANIMACIONES DE ELEMENTOS LEJANOS (LENTOS) ========== */
        /* Estos elementos están DETRÁS del auto (z-5) */
        
        /**
         * Árbol lejano 1 - Velocidad lenta (8s)
         * Se mueve de derecha a izquierda en el fondo
         */
        @keyframes far-tree-1 {
          0% { left: 100%; }
          100% { left: -30px; }
        }
        
        /**
         * Árbol lejano 2 - Velocidad lenta (9s)
         * Ligeramente más lento para variar profundidad
         */
        @keyframes far-tree-2 {
          0% { left: 110%; }
          100% { left: -25px; }
        }
        
        /**
         * Arbusto lejano - Velocidad lenta (7s)
         */
        @keyframes far-bush-1 {
          0% { left: 105%; }
          100% { left: -15px; }
        }
        
        /* ========== ANIMACIONES DE ELEMENTOS CERCANOS (RÁPIDOS) ========== */
        /* Estos elementos cruzan DELANTE del auto (z-30) */
        
        /**
         * Árbol cercano 1 - Velocidad rápida (4s)
         * Más rápido porque está más cerca de la cámara
         */
        @keyframes front-tree-1 {
          0% { left: 100%; }
          100% { left: -50px; }
        }
        
        /**
         * Árbol cercano 2 - Velocidad rápida (5s)
         */
        @keyframes front-tree-2 {
          0% { left: 120%; }
          100% { left: -40px; }
        }
        
        /**
         * Arbusto cercano 1 - Muy rápido (3s)
         * El más rápido para efecto de primer plano
         */
        @keyframes front-bush-1 {
          0% { left: 115%; }
          100% { left: -25px; }
        }
        
        /**
         * Arbusto cercano 2 - Muy rápido (3.5s)
         */
        @keyframes front-bush-2 {
          0% { left: 125%; }
          100% { left: -20px; }
        }
        
        /**
         * Poste/señal - Velocidad media (6s)
         */
        @keyframes front-post-1 {
          0% { left: 108%; }
          100% { left: -5px; }
        }
        
        /* ========== CLASES DE APLICACIÓN DE ANIMACIONES ========== */
        
        .animate-road-moving {
          animation: road-moving 1s linear infinite;
        }
        
        .animate-car-subtle {
          animation: car-subtle 3s ease-in-out infinite;
        }
        
        /* Elementos lejanos con duraciones largas */
        .animate-far-tree-1 {
          animation: far-tree-1 8s linear infinite;
        }
        
        .animate-far-tree-2 {
          animation: far-tree-2 9s linear infinite 3s;
        }
        
        .animate-far-bush-1 {
          animation: far-bush-1 7s linear infinite 2s;
        }
        
        /* Elementos cercanos con duraciones cortas */
        .animate-front-tree-1 {
          animation: front-tree-1 4s linear infinite;
        }
        
        .animate-front-tree-2 {
          animation: front-tree-2 5s linear infinite 2s;
        }
        
        .animate-front-bush-1 {
          animation: front-bush-1 3s linear infinite 1s;
        }
        
        .animate-front-bush-2 {
          animation: front-bush-2 3.5s linear infinite 3s;
        }
        
        .animate-front-post-1 {
          animation: front-post-1 6s linear infinite 4s;
        }
      `}} />
    </div>
  );
};

export default MovingCarAnimation;